import { isNullish } from "@theorgchartdev/storybook";
import {
  enumObjectTypes,
  enumOverrideType,
  kOrphanID,
  kNameID,
  kDesiredHolders,
  kPositionStatus,
  PositionStatus,
  kEmployeePositionID,
  kEmployeeAssignmentStatusID,
  AssignmentStatus,
  enumObjClassNames
} from "@coreapp/constants";
import { FormatFieldValue, sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import type { QChange } from "@coreapp/CoreApp/QChange";

export interface FieldResolutionContext {
  override: QObjDBObject | null;
  change: QChange;
  wsMgr: any;
  objType: ValueOf<typeof enumObjectTypes>;
  doc?: any;
  tableView?: boolean;
  objDB?: any;
}

export interface PositionStatusChangeResult {
  isChange: boolean;
  isClose: boolean;
  isReopen: boolean;
}

export interface AssignmentChangeResult {
  isChange: boolean;
  isAssigned: boolean;
  isUnassigned: boolean;
  isReleased: boolean;
  newPositionID: string | undefined;
}

export const enumChangeText = {
  [enumOverrideType.add]: "Added under",
  [enumOverrideType.move]: "Moved under",
  [enumOverrideType.delete]: "Deleted under",
  [enumOverrideType.change]: "changed to"
};

export const enumAltChangeText = {
  [enumOverrideType.add]: "Created",
  [enumOverrideType.move]: "Moved",
  [enumOverrideType.delete]: "Deleted",
  [enumOverrideType.change]: "Changed"
};

export const enumPositionChangeText = {
  [enumObjectTypes.holder]: {
    [enumOverrideType.add]: "Created and assigned to",
    [enumOverrideType.move]: "Moved under",
    [enumOverrideType.delete]: "Released from",
    [enumOverrideType.change]: "changed to"
  },
  [enumObjectTypes.position]: {
    [enumOverrideType.add]: "Added under",
    [enumOverrideType.move]: "Moved under",
    [enumOverrideType.delete]: "Closed under",
    [enumOverrideType.change]: "changed to"
  }
};

export const GetShortObjLabel = (dbObj: QObjDBObject | null | undefined): string => {
  let label = "";
  if (dbObj?.IsHolderObj()) {
    label = sanitizeBlockedValue(dbObj.SmartGetFieldValue(kNameID), "") as string;
  }

  if (dbObj?.IsPositionObj()) {
    label = FormatPositionLabel(dbObj);
  }

  return label;
};

export const isOrphanChange = (change: QChange | null | undefined): boolean => {
  const delta = (change?.GetDelta() ?? {}) as Record<string, any>;

  return delta["Orphan"] === 1;
};

export const getRawPreviousFieldValue = (
  fieldDef: any,
  {
    override,
    change,
    wsMgr,
    objType
  }: Pick<FieldResolutionContext, "override" | "change" | "wsMgr" | "objType">
) => {
  if (!fieldDef) return null;

  const fieldName = fieldDef.GetFieldName();
  const changes = override?.fChanges;

  if (changes && changes.length > 1) {
    const currentIndex = changes.indexOf(change);
    if (currentIndex > 0) {
      for (let i = currentIndex - 1; i >= 0; i--) {
        const sibling = changes[i];
        if (sibling.IsReverted()) continue;
        if (sibling.fDelta && Object.prototype.hasOwnProperty.call(sibling.fDelta, fieldName)) {
          return sibling.fDelta[fieldName];
        }
      }
    }
  }

  const dbObj = change.fDBObj;
  const planManager = wsMgr?.fPlanManager;

  // Prefer plan manager cache (true source data) over fCloneBase (can reflect post-plan chart state)
  let cloneBase = null;
  if (dbObj) {
    const recordId = dbObj.GetObjectRecordID();
    cloneBase = planManager?.GetCacheBaseRecord(objType, recordId) ?? dbObj.fCloneBase ?? null;
  }

  if (!cloneBase) {
    cloneBase = override?.SmartGetFieldValue?.("displayObject") ?? null;
  }

  return cloneBase?.GetFieldValue(fieldDef, false) ?? null;
};

export const getFormattedOriginalValue = (
  fieldDef: any,
  ctx: Pick<FieldResolutionContext, "override" | "change" | "wsMgr" | "objType">
) => {
  const rawOriginal = getRawPreviousFieldValue(fieldDef, ctx);
  if (rawOriginal == null || rawOriginal === "") return null;
  return FormatFieldValue(rawOriginal, fieldDef) ?? null;
};

/**
 * Only applicable for position planning chart. Checks if position's status has changed from Active to Closed or vice versa.
 * @returns An object containing flags for change, close, and reopen status.
 */
export const CheckForPositionStatusChange = ({
  doc,
  tableView,
  change,
  wsMgr,
  objType
}: {
  doc: any;
  tableView: boolean;
  change: QChange;
  wsMgr: any;
  objType: ValueOf<typeof enumObjectTypes>;
}): PositionStatusChangeResult => {
  if (!(doc.IsPlanChart() || tableView)) {
    return { isChange: false, isClose: false, isReopen: false };
  }
  if (!doc.IsPositionDocument()) {
    return { isChange: false, isClose: false, isReopen: false };
  }

  const objDbClass = doc.GetPositionClass();
  if (!objDbClass) {
    console.error("DBOBJ IS NOT DEFINED");
    return { isChange: false, isClose: false, isReopen: false };
  }
  const delta = change.GetDelta() as Record<string, any>;
  const posStatusFieldDef = objDbClass.GetFieldDefByID(kPositionStatus);
  const desiredHoldersFieldDef = objDbClass.GetFieldDefByID(kDesiredHolders);
  if (!posStatusFieldDef || !desiredHoldersFieldDef) {
    return { isChange: false, isClose: false, isReopen: false };
  }
  const posStatusFieldName = posStatusFieldDef.GetFieldName();
  const desiredHoldersFieldName = desiredHoldersFieldDef.GetFieldName();
  const statusValue = delta?.[posStatusFieldName];
  const targetValue = delta?.[desiredHoldersFieldName];
  const isClose = statusValue === PositionStatus.Closed && targetValue === 0;
  const previousStatus = getRawPreviousFieldValue(posStatusFieldDef, {
    override: change.fParentOverride,
    change,
    wsMgr,
    objType
  });
  const isReopen =
    statusValue === PositionStatus.Active &&
    !isNullish(targetValue) &&
    parseInt(targetValue) > 0 &&
    previousStatus === PositionStatus.Closed;
  return {
    isChange: isClose || isReopen,
    isClose,
    isReopen
  };
};

export const CheckForEmployeeAssignmentChange = ({
  doc,
  tableView,
  objDB,
  override,
  change
}: {
  doc: any;
  tableView?: boolean;
  objDB: any;
  override: QObjDBObject;
  change: QChange;
}): AssignmentChangeResult => {
  const noChange: AssignmentChangeResult = {
    isChange: false,
    isAssigned: false,
    isUnassigned: false,
    isReleased: false,
    newPositionID: undefined
  };

  if (!(doc.IsPlanChart() || tableView)) {
    return noChange;
  }

  if (!doc.IsPositionDocument()) {
    return noChange;
  }

  const delta = change.GetDelta() as Record<string, any>;
  const boxClass = objDB?.GetClassByName(enumObjClassNames.box);
  if (!boxClass) {
    return noChange;
  }

  const empPosFieldName = boxClass.GetFieldDefByID(kEmployeePositionID)?.GetFieldName();
  const assignmentStatusFieldName = boxClass
    .GetFieldDefByID(kEmployeeAssignmentStatusID)
    ?.GetFieldName();

  if (!empPosFieldName || !assignmentStatusFieldName) {
    return noChange;
  }

  const newPositionID = delta?.[empPosFieldName];
  const newAssignmentStatus = delta?.[assignmentStatusFieldName];

  const displayObject: any = override?.SmartGetFieldValue("displayObject");
  const currentAssignmentStatus = displayObject?.SmartGetFieldValue(kEmployeeAssignmentStatusID);
  const effectiveAssignmentStatus =
    newAssignmentStatus !== undefined ? newAssignmentStatus : currentAssignmentStatus;

  const isAssigned =
    effectiveAssignmentStatus === AssignmentStatus.Assigned && !isNullish(newPositionID);
  const isUnassigned = newAssignmentStatus === AssignmentStatus.Unassigned;
  const isReleased = newAssignmentStatus === AssignmentStatus.Released;

  return {
    isChange: isAssigned || isUnassigned || isReleased,
    isAssigned,
    isUnassigned,
    isReleased,
    newPositionID
  };
};

export const getFilteredRows = (rows: any[]) => {
  const suppressionRules = [
    {
      condition: () => true,
      suppressFields: [{ fieldID: kOrphanID }]
    }
  ];

  return rows.filter(row => {
    const fieldID = row.fieldDef?.GetFieldID();

    for (const rule of suppressionRules) {
      if (rule.condition()) {
        const shouldSuppress = rule.suppressFields.some(
          suppressField => suppressField.fieldID === fieldID
        );

        if (shouldSuppress) {
          return false;
        }
      }
    }
    return true;
  });
};

/**
 * Removes rows where the resolved previous value equals the new value.
 * Skips hierarchy/container fields that have dedicated rendering (e.g. "Moved from", "Added under")
 * and fields the change author was blocked from editing (the blocked sentinel arrow still needs to render).
 */
export const filterUnchangedRows = (rows: any[], ctx: FieldResolutionContext) => {
  const { change, doc, objDB } = ctx;
  const hierarchyClass = doc?.GetHierarchyClass();
  const parentFieldName = hierarchyClass?.GetParentIDFieldDef()?.GetFieldName();
  const containerFieldName = objDB
    ?.GetClassByName(enumObjClassNames.box)
    ?.GetParentIDFieldDef()
    ?.GetFieldName();

  return rows.filter(({ fieldDef, displayValue }) => {
    const fieldName = fieldDef?.GetFieldName();
    if (fieldName === parentFieldName || fieldName === containerFieldName) return true;

    if (change.IsAuthorBlockedField(fieldName)) return true;

    const originalFormatted = getFormattedOriginalValue(fieldDef, ctx);
    if (originalFormatted == null || originalFormatted !== displayValue) return true;

    // Resolved original matches display value — field hasn't actually changed
    return false;
  });
};

export const FilterDisplayRowsOnAssignmentChange = (displayRows: any[]) => {
  return displayRows.filter(({ fieldDef }) => {
    const fieldID = fieldDef?.GetFieldID();
    return fieldID !== kEmployeePositionID && fieldID !== kEmployeeAssignmentStatusID;
  });
};

export const FilterDisplayRowsOnPosStatusChange = ({
  rows,
  doc,
  tableView,
  change,
  wsMgr,
  objType
}: {
  rows: any[];
  doc: any;
  tableView: boolean;
  change: QChange;
  wsMgr: any;
  objType: ValueOf<typeof enumObjectTypes>;
}) => {
  let filteredRows = getFilteredRows(rows);
  const { isChange } = CheckForPositionStatusChange({ doc, tableView, change, wsMgr, objType });
  if (isChange) {
    // Keep Desired Holders only if intentionally changed beyond close/reopen
    filteredRows = filteredRows.filter(({ fieldDef, value }) => {
      return fieldDef?.GetFieldID() === kDesiredHolders
        ? !isNullish(value) && parseInt(value) > 1
        : true;
    });

    // Promote position status row to the top
    const posStatusIndex = filteredRows.findIndex(
      ({ fieldDef }) => fieldDef?.GetFieldID() === kPositionStatus
    );
    if (posStatusIndex > -1) {
      const [posStatusRow] = filteredRows.splice(posStatusIndex, 1);
      filteredRows.unshift(posStatusRow);
    }
  }
  return filteredRows;
};

export const resolveGroupPrior = (
  fieldDef: any,
  allChanges: QChange[],
  { wsMgr, objType }: { wsMgr: any; objType: ValueOf<typeof enumObjectTypes> }
) => {
  const priors = new Set();

  for (const change of allChanges) {
    const override = change.fParentOverride;
    const raw = getRawPreviousFieldValue(fieldDef, { override, change, wsMgr, objType });
    const formatted = raw == null || raw === "" ? null : (FormatFieldValue(raw, fieldDef) ?? null);
    priors.add(formatted);
  }

  const nonNullPriors = [...priors].filter(v => v != null);

  if (nonNullPriors.length === 0) {
    return { fromLabel: null, hasOriginal: false, isMultiple: false };
  }

  if (nonNullPriors.length === 1) {
    return { fromLabel: nonNullPriors[0], hasOriginal: true, isMultiple: false };
  }

  return { fromLabel: null, hasOriginal: false, isMultiple: true };
};

export const hasParentChanged = (
  changeType: string,
  {
    originalParentDBObj,
    currentParentDBObj,
    doc
  }: { originalParentDBObj: QObjDBObject | null; currentParentDBObj: QObjDBObject | null; doc: any }
) => {
  // always show current reports to for adds
  if (changeType === enumOverrideType.add && !doc.IsPlanChart()) {
    return false;
  }

  return originalParentDBObj?.GetObjectRecordID() !== currentParentDBObj?.GetObjectRecordID();
};
